<template>
  <button
    ref="buttonRef"
    :class="[
      ns.b(),
      ns.m(buttonType),
      ns.m(buttonSize),
      ns.is('disabled', buttonDisabled as boolean),
      ns.is('loading', loading),
      ns.is('plain', plain),
      ns.is('outline', outline),
      ns.is('round', round),
      ns.is('circle', circle),
      ns.is('text', text),
      ns.is('block', block)
    ]"
    :disabled="buttonDisabled as boolean || loading"
    :type="nativeType"
    :style="buttonStyle"
    @click="handleClick"
  >
    <template v-if="loading">
      <slot v-if="$slots.loading" name="loading"></slot>
      <sg-icon v-else :class="ns.is('loading')">
        <component :is="loadingIcon" />
      </sg-icon>
    </template>
    <sg-icon v-else-if="icon">
      <component :is="icon" />
    </sg-icon>
    <span v-if="$slots.default">
      <slot></slot>
    </span>
  </button>
</template>

<script lang="ts">
import { computed, inject, defineComponent, ref } from 'vue'
import { useCssVar } from '@vueuse/core'
import { TinyColor } from '@ctrl/tinycolor'
import { SgIcon } from '@sgui-plus/components/icon'
import {
  useDisabled,
  useFormItem,
  useNamespace,
  useSize
} from '@sgui-plus/hooks'
import { Loading } from '@sgui-plus/icons-vue'

import { ButtonGroupContextKey } from './constants'
import { buttonEmits, buttonProps } from './button'

export default defineComponent({
  name: 'SgButton',

  components: {
    SgIcon,
    Loading,
  },

  props: buttonProps,
  emits: buttonEmits,

  setup(props, { emit }) {
    const buttonRef = ref()
    const buttonGroupContext = inject(ButtonGroupContextKey, undefined)
    const ns = useNamespace('button')

    const { form } = useFormItem()
    const buttonSize = useSize(computed(() => buttonGroupContext?.size) as any)
    const buttonDisabled = useDisabled()
    const buttonType = computed(
      () => props.type || buttonGroupContext?.type || ''
    )

    // calculate hover & active color by color
    const typeColor = computed(
      () => useCssVar(`--sg-color-${props.type}`).value
    )
    const buttonStyle = computed(() => {
      let styles = {}

      const buttonColor = props.color || typeColor.value

      if (buttonColor) {
        const shadeBgColor = new TinyColor(buttonColor).shade(10).toString()
        if (props.plain || props.outline) {
          styles = {
            '--sg-button-bg-color':props.plain ? new TinyColor(buttonColor)
              .tint(90)
              .toString() :
              'var(--sg-color-white)',
            '--sg-button-text-color': buttonColor,
            '--sg-button-hover-text-color': 'var(--sg-color-white)',
            '--sg-button-hover-bg-color': buttonColor,
            '--sg-button-hover-border-color': buttonColor,
            '--sg-button-active-bg-color': shadeBgColor,
            '--sg-button-active-text-color': 'var(--sg-color-white)',
            '--sg-button-active-border-color': shadeBgColor,
          }
        } else {
          const tintBgColor = new TinyColor(buttonColor).tint(20).toString()
          styles = {
            '--sg-button-bg-color': buttonColor,
            '--sg-button-border-color': buttonColor,
            '--sg-button-hover-bg-color': tintBgColor,
            '--sg-button-hover-border-color': tintBgColor,
            '--sg-button-active-bg-color': shadeBgColor,
            '--sg-button-active-border-color': shadeBgColor,
          }
        }

        if (buttonDisabled.value) {
          const disabledButtonColor = new TinyColor(buttonColor)
            .tint(50)
            .toString()
          styles['--sg-button-disabled-bg-color'] = disabledButtonColor
          styles['--sg-button-disabled-border-color'] = disabledButtonColor
        }
      }

      return styles
    })

    const handleClick = (evt: MouseEvent) => {
      if (props.nativeType === 'reset') {
        form?.resetFields()
      }
      emit('click', evt)
    }

    return {
      buttonRef,
      buttonStyle,

      buttonSize,
      buttonType,
      buttonDisabled,

      handleClick,

      ns
    }
  },
})
</script>
